import React, { useState, useEffect, useContext } from 'react'
import { node, func, object } from 'prop-types'
import { navigate } from 'gatsby'
import omit from 'lodash/omit'
import moment from 'moment'

import { trackEvent } from 'utils/tracking'
import { PATHS_WITHOUT_SOFT_AV, PROTECTED_ROUTES } from 'utils/constants'
import * as NativeAuth from '../lib/auth-sdk'
import { checkoutUpdateCustomAttribute } from 'queries/checkout'
import useDigitalData from 'hooks/useDigitalData'
import CheckoutContext from 'contexts/Checkout'

const NativeAuthContext = React.createContext()

const NativeAuthProvider = ({ children, checkout, setAuthSdk, setCheckoutUser }) => {
  const { removeSubscriptionItemsFromCheckout, setDraftOrder } = useContext(CheckoutContext)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(process.browser && localStorage.user ? JSON.parse(localStorage.user) : null)
  const [province, setProvince] = useState(process.browser && localStorage.province ? localStorage.province : null)
  const [token, setToken] = useState(process.browser && localStorage.token ? localStorage.token : null)
  const [signupError, setSignupError] = useState('')
  const [loginError, setLoginError] = useState('')
  const [signUpModalVisibility, setSignupModalVisibility] = useState(false)
  const [loginModalVisibility, setLoginModalVisibility] = useState(false)
  const [missingAddressModalVisibility, setMissingAddressModalVisibility] = useState(false)
  const [cancelSubscriptionModalVisibility, setCancelSubscriptionModalVisibility] = useState(false)
  const [pauseSubscriptionModalVisibility, setPauseSubscriptionModalVisibility] = useState(false)
  const [subscriptionModalVisible, setSubscriptionModalVisible] = useState(false)
  const [verifyEmailModalVisibility, setVerifyEmailModalVisibility] = useState(false)
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false)
  const [vypePlusDiscountCodesModalVisibility, setVypePlusDiscountCodesModalVisibility] = useState(false)
  const [participatesTo2020Lottery, setParticipatesTo2020Lottery] = useState(false)
  const [isBoostPlanModalVisible, setIsBoostPlanModalVisible] = useState(false)

  const { digitalData } = useDigitalData()

  useEffect(() => {
    const init = async () => {
      NativeAuth.init({
        storeName: 'vype',
        name: 'vype',
        website: 'govype.ca',
        phone: '1-844-746-8973',
        email: 'info@govype.ca',
        authApiUrl: process.env.GATSBY_AUTH_API,
        frontend: process.env.AUTH_FRONTEND
      })

      NativeAuth.iframe.addEventListener('load', initialAuthCheck)
      setAuthSdk(NativeAuth)
    }

    init()

    if (window.localStorage?.province === 'QC') {
      navigate('/qc')
    }
  }, [])

  useEffect(() => {
    if (participatesTo2020Lottery && user && token) {
      updateUser({ participatesTo2020Lottery })
      setParticipatesTo2020Lottery(false)
    }
  }, [user, token, participatesTo2020Lottery])

  useEffect(() => {
    setCheckoutUser(user)
    if (!user && window.location.pathname.includes('/account')) {
      navigate('/')
    }
  }, [user])

  useEffect(() => {
    if (!user) return

    digitalData.push({
      user: {
        userInfo: {
          age: moment(user.dob).diff(moment(), 'years'),
          gender: '',
          memberId: user.id,
          emailVerified: user.emailVerified,
          postalCode: user.postalCode,
          stateProvince: user.province
        },
        loyaltyInfo: {
          memberSince: user.silverTierDate || user.goldTierDate || user.platinumTierDate || '',
          newsletterSubscribed: user.emailSubscriptionVype,
          lifetimeSpend: parseFloat(user.totalSpentAmount || '0.00').toFixed(2),
          status: user.currentTierLevel || ''
        },
        subscriptionInfo: {
          program: ''
        }
      }
    })
  }, [user])

  const initialAuthCheck = async () => {
    if (localStorage.softVerifiedUser) setLoading(false)

    let [{ user, token }, { softVerifiedUser }] = await Promise.all([
      NativeAuth.getUserState(),
      NativeAuth.getSoftVerifiedUser()
    ])

    setUser(user)
    setToken(token)
    setProvince(user?.province || softVerifiedUser?.user?.province)

    // Also re-fetch the user from the server to update the SDK's cache
    if (token) {
      NativeAuth.getUser(token)
        .then(({ user, token }) => {
          setUser(user)
          localStorage.setItem('user', JSON.stringify(user))
          setToken(token)
          setProvince(user?.province || softVerifiedUser?.user?.province)

          // window.ire('identify', {
          //   customerId: user.id,
          //   customerEmail: sha1(user.email)
          // })

          return
        })
        .catch(() => {
          NativeAuth.logout()

          setUser(null)
          setToken(null)
          localStorage.removeItem('user')
          localStorage.removeItem('token')

          if (softVerifiedUser) {
            setProvince(softVerifiedUser.user.province)
            localStorage.setItem('province', softVerifiedUser.user.province)
          } else {
            setProvince(null)
            localStorage.removeItem('province')
          }
        })
    }

    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
      localStorage.setItem('province', user.province)
      localStorage.setItem('token', token)

      window.dataObject.user = {
        userID: user.id,
        verifiedEmail: user.emailVerified
      }
    } else if (softVerifiedUser && softVerifiedUser.user) {
      localStorage.setItem('softVerifiedUser', JSON.stringify(softVerifiedUser.user))
      localStorage.setItem('province', softVerifiedUser.user.province)
      localStorage.removeItem('user')
      localStorage.removeItem('token')

      // window.ire('identify', { customerId: '', customerEmail: '' })
    } else {
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      localStorage.removeItem('province')
      localStorage.removeItem('softVerifiedUser')

      const skipSoftAV =
        PATHS_WITHOUT_SOFT_AV.some(path => location.pathname.startsWith(process.env.BASE_PATH + path)) ||
        /bot|google|baidu|bing|msn|duckduckbot|teoma|slurp|yandex/i.test(navigator.userAgent) ||
        window.location.search.includes('beyond')

      if (!skipSoftAV) {
        // Intentionally left as a non-prefixed URL to prevent loops (it's client-side only)
        navigate(`/soft-verify?redirectTo=${btoa(location.pathname + location.search)}`)
      }

      // window.ire('identify', { customerId: '', customerEmail: '' })
    }

    setLoading(false)
  }

  const login = async ({ email, password }) => {
    try {
      const response = await NativeAuth.login(email, password)
      setUser(response.user)
      setToken(response.token)
      setCheckoutJwtToken(response.token)
      setProvince(response.user.province)
      localStorage.setItem('token', response.token)
      localStorage.setItem('user', JSON.stringify(response.user))
      localStorage.setItem('province', response.user.province)
      localStorage.setItem('urlAtLogin', location.pathname)
      setLoginError('')
      setLoginModalVisibility(false)
      setParticipatesTo2020Lottery(false)

      if (response.user.province === 'ON') {
        if (location.pathname === '/') navigate('/ontario/')
      }

      if (response.user.province === 'BC') {
        if (location.pathname === '/') navigate('/bc/')
      }

      trackEvent({
        name: 'loginTrue',
        time: Date()
      })

      window.dataObject.user = {
        userID: response.user.id,
        verifiedEmail: response.user.emailVerified
      }

      // window.ire('identify', {
      //   customerId: user.id,
      //   customerEmail: sha1(user.email)
      // })
    } catch (e) {
      if (e?.status === 401 || e?.response?.status === 401) {
        setLoginError('Email or password incorrect')
      } else {
        setLoginError('An unknown error has occured. Please try again later.')
      }
    }
  }

  const signup = async user => {
    if (!user.email.match(/@(volume7|bat|deloitte|deloittece|yopmail)\.(io|ca|com|co\.uk)$/)) {
      const response = await NativeAuth.screenEmail(user.email)

      if (response.error === 'Email account invalid') {
        setSignupError('emailAccountInvalid')
        return 'emailAccountInvalid'
      }
    }

    try {
      const response = await NativeAuth.signup({
        user: {
          ...omit(user, ['passwordConfirm', 'addressLabel', 'street', 'manualEntry', 'isManualEntry']),
          address: user.street,
          participatesTo2020Lottery
        },
        storeName: 'vype'
      })

      if (response.method === 'idVerification') {
        setSignupError('idVerification')
        return 'idVerification'
      } else {
        setUser(response.user)
        setToken(response.token)
        setCheckoutJwtToken(response.token)
        setProvince(response.user.province)
        localStorage.setItem('token', response.token)
        localStorage.setItem('user', JSON.stringify(response.user))
        localStorage.setItem('province', JSON.stringify(response.user.province))
        setSignupError('')

        if (response.user.province === 'ON') {
          if (location.pathname === '/') navigate('/ontario/')
        }

        if (response.user.province === 'BC') {
          if (location.pathname === '/') navigate('/bc/')
        }

        window.dataLayer.push({
          event: 'formSubmitHardAgeValidation',
          eventCategory: 'Activate',
          eventAction: 'Soft conversion',
          eventLabel: 'Hard age validation',
          pagePath: '/epod',
          language: 'En',
          province: user.province,
          connexion: 'yes'
        })
      }
    } catch (e) {
      console.log(e)
      if (e.data === 'emailAlreadyExists' || e.data.message === 'emailAlreadyExists') {
        setSignupError('emailAlreadyExists')
        return 'emailAlreadyExists'
      } else if (e.data === 'accountInvalid' || e.data.message === 'accountInvalid') {
        setSignupError('accountInvalid')
        return 'accountInvalid'
      } else {
        return e.data
      }
    }
  }

  const setNativeSoftVerifiedUser = async (user, redirectAfter = true) => {
    try {
      NativeAuth.setSoftVerifyUser({ user })
      localStorage.setItem('softVerifiedUser', JSON.stringify(user))
      localStorage.setItem('province', user.province)
      setProvince(user.province)

      if (redirectAfter) {
        if (user.province === 'QC') {
          navigate('/qc/')
        } else if (user.province === 'ON') {
          if (location.pathname === '/') navigate('/ontario/')
        } else if (user.province === 'BC') {
          if (location.pathname === '/') navigate('/bc/')
        }
      }
    } catch (e) {
      console.log(e)
    }
  }

  const logout = () => {
    NativeAuth.logout()
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('urlAtLogin')
    setUser(null)
    setToken(null)
    if (localStorage.softVerifiedUser) {
      setProvince(JSON.parse(localStorage.softVerifiedUser).province)
    }
    if (PROTECTED_ROUTES.includes(location.pathname)) {
      navigate('/')
    }
    delete window.dataObject.user
    // window.ire('identify', { customerId: '', customerEmail: '' })
  }

  const updateUser = async body => {
    setLoading(true)
    const user = await NativeAuth.updateUser(body, token)
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('province', JSON.stringify(user.province))
    setUser(user)
    setProvince(user.province)
    setLoading(false)
    return user
  }

  const forgotPassword = async body => {
    await NativeAuth.forgotPassword(body)
    setForgotPasswordSuccess(true)
  }

  const resetPassword = NativeAuth.resetPassword

  const setCheckoutJwtToken = async token => {
    await checkoutUpdateCustomAttribute({
      checkoutId: checkout.id,
      input: {
        customAttributes: [
          {
            key: 'jwt',
            value: token
          }
        ]
      }
    })
  }

  const verifyEmail = async body => {
    await NativeAuth.verifyEmail(body)

    if (user) {
      setUser({ ...user, emailVerified: true })
      localStorage.setItem('user', JSON.stringify({ ...user, emailVerified: true }))
      NativeAuth.updateCache({ user: { ...user, emailVerified: true } })
    }
  }

  const addPaymentMethod = async body => {
    await NativeAuth.addPaymentMethod(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
  }

  const deletePaymentMethod = async body => {
    await NativeAuth.deletePaymentMethod(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
  }

  const editSubscription = async body => {
    setLoading(true)
    await NativeAuth.editSubscription(omit(body, ['silent']))
    const { user } = await NativeAuth.getUser(token)
    setUser(user)

    if (!body.silent) {
      navigate('/account/subscriptions/?subscriptionStatus=updated')
    }

    setLoading(false)
  }

  const cancelSubscription = async body => {
    await NativeAuth.cancelSubscription(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
  }

  const cancelOrder = async body => {
    setLoading(true)
    await NativeAuth.cancelOrder(body)
    const { user } = await NativeAuth.getUser(token)
    setUser(user)
    setLoading(false)
  }

  const getOrder = async orderId => {
    const { order } = await NativeAuth.getOrder({ orderId, token })
    return order
  }

  const submitCalloutApplication = async data => {
    await NativeAuth.submitCalloutApplication({ data, token })
  }

  const createBoostPlanOrder = async data => {
    const { order } = await NativeAuth.createBoostPlanOrder({ data, token })
    return order
  }

  const submitCalloutVotes = async data => {
    const { callout_vote_options: calloutVoteOptions } = data
    await NativeAuth.submitCalloutVotes(data, token)
    setUser({
      ...user,
      calloutVoteOptions
    })
  }

  const claimTransitionEpod = async data => {
    const { order } = await NativeAuth.claimTransitionEpod(data, token)
    return order
  }

  const editSubscriptionWithDraftOrder = async ({ items, redirect = true }) => {
    await editSubscription({
      id: user?.subscription.id,
      token,
      items: items.map(item => ({
        subscriptionId: user?.subscription.id,
        variantId: item.lineItemData.variant.id,
        quantity: item.quantity,
        lineItemData: item.lineItemData
      }))
    })

    await removeSubscriptionItemsFromCheckout()

    setDraftOrder({})

    if (redirect) {
      navigate('/account/subscriptions/?subscriptionStatus=updated')
    }
  }

  const validateEmail = NativeAuth.validateEmail

  const value = {
    loading,
    user,
    setUser,
    token,
    province,
    login,
    signup,
    updateUser,
    logout,
    loginError,
    signupError,
    setSignupError,
    screenEmail: NativeAuth?.screenEmail,
    setNativeSoftVerifiedUser,
    signUpModalVisibility,
    setSignupModalVisibility,
    loginModalVisibility,
    setLoginModalVisibility,
    missingAddressModalVisibility,
    setMissingAddressModalVisibility,
    verifyEmailModalVisibility,
    setVerifyEmailModalVisibility,
    verifyEmail,
    sendVypeVerificationEmail: NativeAuth?.sendVypeVerificationEmail,
    forgotPassword,
    forgotPasswordSuccess,
    resetPassword,
    editSubscription,
    cancelSubscription,
    cancelSubscriptionModalVisibility,
    setCancelSubscriptionModalVisibility,
    pauseSubscriptionModalVisibility,
    setPauseSubscriptionModalVisibility,
    vypePlusDiscountCodesModalVisibility,
    setVypePlusDiscountCodesModalVisibility,
    participatesTo2020Lottery,
    setParticipatesTo2020Lottery,
    cancelOrder,
    deletePaymentMethod,
    addPaymentMethod,
    authSdk: NativeAuth,
    getOrder,
    submitCalloutApplication,
    setIsBoostPlanModalVisible,
    isBoostPlanModalVisible,
    createBoostPlanOrder,
    validateEmail,
    setProvince,
    claimTransitionEpod,
    submitCalloutVotes,
    subscriptionModalVisible,
    setSubscriptionModalVisible,
    editSubscriptionWithDraftOrder
  }

  return <NativeAuthContext.Provider value={value}>{children}</NativeAuthContext.Provider>
}

NativeAuthProvider.propTypes = {
  children: node.isRequired,
  checkout: object,
  setAuthSdk: func,
  setCheckoutUser: func
}

const { Consumer } = NativeAuthContext

export { Consumer as NativeAuthConsumer, NativeAuthProvider }

export default NativeAuthContext
